import React, { useCallback, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Space,
	Switch,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import {
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	ReloadOutlined,
	SettingOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { leaveService, ILeaveTypeConfig } from '../../common/service'

interface LeaveSettingsProps {
	embedded?: boolean
}

const LeaveSettings: React.FC<LeaveSettingsProps> = ({ embedded = false }) => {
	const { isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary, text, textSecondary, bg, border } = themeColors
	const navigate = useNavigate()

	// Cambodia Labour Law defaults
	const CAMBODIA_DEFAULTS: ILeaveTypeConfig[] = [
		{
			key: 'Annual',
			label: 'Annual Leave',
			defaultEntitlement: 18,
			requiresAttachment: false,
			enabled: true,
		},
		{
			key: 'Sick',
			label: 'Sick Leave',
			defaultEntitlement: 180,
			requiresAttachment: true,
			enabled: true,
		},
		{
			key: 'Maternity',
			label: 'Maternity Leave',
			defaultEntitlement: 84,
			requiresAttachment: false,
			enabled: true,
		},
		{
			key: 'Special',
			label: 'Special Leave',
			defaultEntitlement: 7,
			requiresAttachment: false,
			enabled: true,
		},
		{
			key: 'Unpaid',
			label: 'Unpaid Leave',
			defaultEntitlement: 0,
			requiresAttachment: false,
			enabled: true,
		},
		{
			key: 'Paternity',
			label: 'Paternity Leave',
			defaultEntitlement: 7,
			requiresAttachment: false,
			enabled: true,
		},
	]

	const [types, setTypes] = useState<ILeaveTypeConfig[]>([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [dirty, setDirty] = useState(false)

	// Add / Edit modal
	const [modalOpen, setModalOpen] = useState(false)
	const [editIndex, setEditIndex] = useState<number | null>(null)
	const [form] = Form.useForm()

	// ── Fetch ──────────────────────────────────────────────────────────────
	const fetchSettings = useCallback(async () => {
		setLoading(true)
		setDirty(false)
		try {
			const res = await leaveService.getLeaveSettings()
			const list: ILeaveTypeConfig[] = res?.data?.types ?? []
			setTypes(list)
		} catch {
			message.error('Failed to load leave settings')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchSettings()
	}, [fetchSettings])

	// ── Save all ───────────────────────────────────────────────────────────
	const handleSave = async () => {
		setSaving(true)
		try {
			await leaveService.setLeaveSettings(types)
			message.success(`${types.length} leave types saved`)
			setDirty(false)
		} catch {
			message.error('Failed to save leave settings')
		} finally {
			setSaving(false)
		}
	}

	// ── Back navigation (guard unsaved changes) ────────────────────────────
	const handleBack = () => {
		if (dirty) {
			Modal.confirm({
				title: 'Unsaved Changes',
				content: 'You have unsaved changes that will be lost. Save before leaving?',
				okText: 'Save & Leave',
				cancelText: 'Leave without Saving',
				onOk: async () => {
					await handleSave()
					navigate('/leaves')
				},
				onCancel: () => navigate('/leaves'),
			})
		} else {
			navigate('/leaves')
		}
	}

	// ── Add / Edit modal ───────────────────────────────────────────────────
	const openAdd = () => {
		setEditIndex(null)
		form.resetFields()
		form.setFieldsValue({
			defaultEntitlement: 0,
			requiresAttachment: false,
			enabled: true,
			carryForward: false,
			carryForwardCap: undefined,
		})
		setModalOpen(true)
	}

	const openEdit = (index: number) => {
		setEditIndex(index)
		const t = types[index]
		form.setFieldsValue({
			key: t.key,
			label: t.label,
			defaultEntitlement: t.defaultEntitlement,
			requiresAttachment: t.requiresAttachment,
			enabled: t.enabled,
			carryForward: t.carryForward ?? false,
			carryForwardCap: t.carryForwardCap ?? undefined,
		})
		setModalOpen(true)
	}

	const handleModalOk = () => {
		form.validateFields().then((values) => {
			const entry: ILeaveTypeConfig = {
				key: values.key.trim(),
				label: values.label.trim(),
				defaultEntitlement: values.defaultEntitlement ?? 0,
				requiresAttachment: values.requiresAttachment ?? false,
				enabled: values.enabled ?? true,
				carryForward: values.carryForward ?? false,
				carryForwardCap:
					values.carryForward && values.carryForwardCap != null
						? values.carryForwardCap
						: undefined,
			}
			setTypes((prev) => {
				const updated = [...prev]
				if (editIndex !== null) {
					updated[editIndex] = entry
				} else {
					updated.push(entry)
				}
				return updated
			})
			setDirty(true)
			setModalOpen(false)
		})
	}

	const handleDelete = (index: number) => {
		setTypes((prev) => prev.filter((_, i) => i !== index))
		setDirty(true)
	}

	const handleToggleEnabled = (index: number, enabled: boolean) => {
		setTypes((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], enabled }
			return updated
		})
		setDirty(true)
	}

	// ── Reset to Cambodia defaults ─────────────────────────────────────────
	const handleReset = () => {
		Modal.confirm({
			title: 'Reset to Cambodia Labour Law Defaults?',
			content:
				'This will replace all current leave types with the Cambodia Labour Law defaults. Click Save Changes afterward to persist.',
			okText: 'Reset',
			okButtonProps: { danger: true },
			onOk: () => {
				setTypes(CAMBODIA_DEFAULTS)
				setDirty(true)
			},
		})
	}

	// ── Table columns ──────────────────────────────────────────────────────
	const columns: ColumnsType<ILeaveTypeConfig> = [
		{
			title: '#',
			width: 50,
			render: (_v, _r, i) => i + 1,
		},
		{
			title: 'Key (API value)',
			dataIndex: 'key',
			width: 140,
			render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
		},
		{
			title: 'Display Label',
			dataIndex: 'label',
		},
		{
			title: 'Default Entitlement (days)',
			dataIndex: 'defaultEntitlement',
			width: 200,
			render: (v: number) => `${v} day${v !== 1 ? 's' : ''}`,
		},
		{
			title: 'Requires Attachment',
			dataIndex: 'requiresAttachment',
			width: 180,
			render: (v: boolean) =>
				v ? <Tag color='orange'>Required</Tag> : <Tag color='default'>Not required</Tag>,
		},
		{
			title: 'Carry Forward',
			dataIndex: 'carryForward',
			width: 120,
			render: (v: boolean) =>
				v ? <Tag color='blue'>Enabled</Tag> : <Tag color='default'>Disabled</Tag>,
		},
		{
			title: 'CF Cap (days)',
			dataIndex: 'carryForwardCap',
			width: 120,
			render: (v: number | undefined) =>
				v != null ? `${v}d` : <Typography.Text type='secondary'>—</Typography.Text>,
		},
		{
			title: 'Enabled',
			dataIndex: 'enabled',
			width: 90,
			render: (v: boolean, _r, i) => (
				<Switch
					size='small'
					checked={v}
					onChange={(checked) => handleToggleEnabled(i, checked)}
				/>
			),
		},
		{
			title: 'Actions',
			width: 100,
			render: (_v, _r, i) => (
				<Space>
					<Tooltip title='Edit'>
						<Button
							type='text'
							icon={<EditOutlined />}
							size='small'
							onClick={() => openEdit(i)}
						/>
					</Tooltip>
					<Popconfirm
						title='Remove this leave type?'
						onConfirm={() => handleDelete(i)}
						okText='Remove'
						okButtonProps={{ danger: true }}
					>
						<Button danger type='text' icon={<DeleteOutlined />} size='small' />
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<>
			{!embedded && <GlobalHeader />}

			{/* ── Page Header ─────────────────────────────────────────────── */}
			<div
				style={{
					background: bg,
					borderBottom: `1px solid ${border}`,
					padding: '14px 24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: 10,
							background: isDarkMode ? `${primary}15` : primary,
							color: isDarkMode ? primary : '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<SettingOutlined style={{ fontSize: 24 }} />
					</div>
					<div>
						<div
							style={{
								fontSize: 20,
								fontWeight: 700,
								color: text,
								lineHeight: '1.2',
							}}
						>
							Leave Type Settings
						</div>
						<div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
							{types.filter((t) => t.enabled).length} of {types.length} leave type
							{types.length !== 1 ? 's' : ''} enabled
						</div>
					</div>
				</div>
				<Space size={8}>
					<Button icon={<PlusOutlined />} onClick={openAdd}>
						Add Leave Type
					</Button>
					<Button icon={<ReloadOutlined />} onClick={handleReset}>
						Reset to Cambodia Defaults
					</Button>
					<Button type='primary' loading={saving} disabled={!dirty} onClick={handleSave}>
						Save Changes
					</Button>
					{!embedded ? <Button onClick={handleBack}>← Back to Leaves</Button> : null}
				</Space>
			</div>

			{dirty && (
				<div style={{ padding: '8px 24px 0' }}>
					<Card
						size='small'
						style={{ borderRadius: 8, background: '#fffbe6', borderColor: '#ffe58f' }}
					>
						<Typography.Text type='warning'>
							You have unsaved changes. Click <strong>Save Changes</strong> to persist
							them.
						</Typography.Text>
					</Card>
				</div>
			)}

			{/* ── Table ───────────────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					<Table<ILeaveTypeConfig>
						columns={columns}
						dataSource={types}
						rowKey={(t) => t.key}
						loading={loading}
						pagination={false}
						size='small'
						scroll={{ x: 800 }}
					/>
				</Card>
			</div>

			{/* Add / Edit Modal */}
			<Modal
				open={modalOpen}
				title={editIndex !== null ? 'Edit Leave Type' : 'Add Leave Type'}
				onCancel={() => setModalOpen(false)}
				onOk={handleModalOk}
				okText='Save'
				destroyOnClose
			>
				<Form form={form} layout='vertical'>
					<Form.Item
						label='Key (API value)'
						name='key'
						rules={[{ required: true, message: 'Please enter a key' }]}
						extra='Used internally and in the mobile app. Changing this affects existing leave records.'
					>
						<Input placeholder='e.g. Annual' disabled={editIndex !== null} />
					</Form.Item>
					<Form.Item
						label='Display Label'
						name='label'
						rules={[{ required: true, message: 'Please enter a label' }]}
					>
						<Input placeholder='e.g. Annual Leave' />
					</Form.Item>
					<Form.Item
						label='Default Entitlement (days)'
						name='defaultEntitlement'
						rules={[{ required: true, message: 'Please enter entitlement days' }]}
					>
						<InputNumber min={0} max={365} style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item
						label='Requires Attachment (e.g. medical certificate)'
						name='requiresAttachment'
						valuePropName='checked'
					>
						<Switch />
					</Form.Item>
					<Form.Item label='Enabled' name='enabled' valuePropName='checked'>
						<Switch />
					</Form.Item>{' '}
					<Form.Item
						label='Allow Carry Forward (roll unused days to next year)'
						name='carryForward'
						valuePropName='checked'
					>
						<Switch />
					</Form.Item>
					<Form.Item
						noStyle
						shouldUpdate={(prev, curr) => prev.carryForward !== curr.carryForward}
					>
						{({ getFieldValue }) =>
							getFieldValue('carryForward') ? (
								<Form.Item
									label='Carry Forward Cap (days)'
									name='carryForwardCap'
									extra='Maximum days that can roll over. Leave blank to use Default Entitlement as cap.'
								>
									<InputNumber
										min={0}
										max={365}
										style={{ width: '100%' }}
										placeholder='e.g. 5'
									/>
								</Form.Item>
							) : null
						}
					</Form.Item>{' '}
				</Form>
			</Modal>
		</>
	)
}

export default LeaveSettings
